import { Formik, Form, useFormikContext } from 'formik';

import { Widget, WidgetBody } from '@@/Widget';

import { TypedPolicy } from '../../../types';
import { PolicyConfigurationFormSection } from '../../PolicyConfigurationFormSection';
import { PolicyFormFooter } from '../../PolicyFormFooter';

import { KubernetesRegistryFormFields } from './KubernetesRegistryFormFields';
import { KubernetesRegistryFormValues } from './types';
import { useKubernetesRegistryInitialValues } from './useKubernetesRegistryInitialValues';
import { kubernetesRegistryValidationSchema } from './kubernetesRegistryValidationSchema';

interface Props {
  /** Form submission handler */
  onSubmit: (values: KubernetesRegistryFormValues) => Promise<void>;
  /** Form mode - affects button labels and validation behavior */
  mode: 'create' | 'edit';
  /** Existing policy for edit mode - provides initial values */
  policy?: TypedPolicy<unknown>;
  /** Wizard back navigation - only used in create mode */
  onBack?: () => void;
  /** Whether back is enabled - only used in create mode */
  canGoBack?: boolean;
}

/**
 * Kubernetes Registry Policy Form Component
 *
 * Used by:
 * - PolicyCreateView (step 2): mode="create" with wizard navigation props
 * - ConfigTab (edit view): mode="edit" without wizard navigation
 */
export function KubernetesRegistryPolicyForm({
  onSubmit,
  mode,
  policy,
  onBack,
  canGoBack,
}: Props) {
  const initialValues = useKubernetesRegistryInitialValues(policy);
  const originalEnvironmentGroupIds = policy?.EnvironmentGroups || [];

  return (
    <Formik<KubernetesRegistryFormValues>
      initialValues={initialValues}
      validationSchema={kubernetesRegistryValidationSchema}
      onSubmit={onSubmit}
      enableReinitialize={mode === 'edit'}
      validateOnMount
    >
      <KubernetesRegistryPolicyInnerForm
        mode={mode}
        policyId={policy?.Id ?? null}
        originalEnvironmentGroupIds={originalEnvironmentGroupIds}
        onBack={onBack}
        canGoBack={canGoBack}
      />
    </Formik>
  );
}

/**
 * Inner component that has access to Formik context.
 * Renders Widget with form fields and PolicyFormFooter.
 */
function KubernetesRegistryPolicyInnerForm({
  mode,
  policyId,
  originalEnvironmentGroupIds,
  onBack,
  canGoBack,
}: {
  mode: 'create' | 'edit';
  policyId: number | null;
  originalEnvironmentGroupIds: Array<number>;
  onBack?: () => void;
  canGoBack?: boolean;
}) {
  const { values, setFieldValue } =
    useFormikContext<KubernetesRegistryFormValues>();

  return (
    <Form className="form-horizontal">
      <Widget>
        <WidgetBody>
          {mode === 'create' && (
            <PolicyConfigurationFormSection policyType="registry-k8s" />
          )}
          <KubernetesRegistryFormFields
            formData={values}
            onChange={(key, value) => setFieldValue(key, value)}
            mode={mode}
          />
          {/* Edit mode: footer inside Widget */}
          {mode === 'edit' && (
            <PolicyFormFooter
              mode={mode}
              policyType="registry-k8s"
              policyId={policyId}
              originalEnvironmentGroupIds={originalEnvironmentGroupIds}
            />
          )}
        </WidgetBody>
      </Widget>

      {/* Create mode: footer outside Widget with StickyFooter */}
      {mode === 'create' && (
        <PolicyFormFooter
          mode={mode}
          policyType="registry-k8s"
          policyId={policyId}
          onBack={onBack}
          canGoBack={canGoBack}
        />
      )}
    </Form>
  );
}
